import React, { useEffect, useMemo, useState } from 'react';
import Bar from '@common/components/bar';
import { Container } from '@common/components/container';
import Overview from '@common/components/overview';
import DataTable from '@common/components/datatable';
import { useRequest } from '@common/services/api';
import { useAppSelector, useDidComponentMount } from '@common/hooks';
import { selected } from '@modules/organisation/selectors/organisation';
import { omit } from 'lodash';
import Placeholder from '@common/components/placeholder';
import { t } from 'i18next';
import * as organisationSelectors from '../../../organisation/selectors/organisation';
import * as userSelector from '../../../core/selectors/logged-user';
import useColumns from './useColumns';
import { RecognitionMeta, RecognitionObject } from './types';
import FiltersToolbar from '@common/components/filters-toolbar/filters-toolbar';
import { usePredicateState } from '@common/hooks/use-predicate-state';
import { TopNavigationBar } from '@common/components/navigation-bar';
import { EPredicateFilters } from '@common/components/predicates-filter';
import { EPredicateFields } from '@common/definitions';
import { useLocation, useParams } from 'react-router-dom';

const HighFiveContainer = () => {
  const organisation = useAppSelector(selected);
  const didMount = useDidComponentMount();
  const [search, setSearch] = useState<string>('');
  const networks = useAppSelector(organisationSelectors.networks);
  const functions = useAppSelector(organisationSelectors.functions);
  const loggedUser = useAppSelector(userSelector.selected);
  const location = useLocation();
  const { networkId } = useParams<{ networkId?: string }>();
  const [predicates, setPredicates] = usePredicateState({
    networks,
    functions,
  });

  const [networkIds, functionIds] = useMemo(() => {
    return [
      predicates.find(
        (p) => p.filter === EPredicateFilters[EPredicateFields.NETWORK]
      )?.value.value,
      predicates.find(
        (p) => p.filter === EPredicateFilters[EPredicateFields.FUNCTION]
      )?.value.value,
    ];
  }, [predicates]);

  const isNetworkView = location.pathname.includes('/networks/');
  const payload = useMemo(
    () => ({
      ...(networkIds ? { network_ids: networkIds } : {}),
      ...(functionIds ? { function_ids: functionIds } : {}),
      ...(search && search !== '' ? { q: search } : {}),
    }),
    [networkIds, functionIds, search]
  );


  const apiConfig = {
    useCursor: true,
    limit: 20,
    payload,
    transform: (res: any) => {
      return {
        ...res,
        data: res.data.map((recognition: RecognitionObject) => ({
          ...recognition,
          sender_id: recognition.sender_id
            ? res.meta.related.users.find(
                (user: any) => user.id === recognition.sender_id
              )
            : null,
          recipient_id: recognition.recipient_id
            ? res.meta.related.users.find(
                (user: any) => user.id === recognition.recipient_id
              )
            : null,
        })),
      };
    },
  };

  const apiUrl = useMemo(() => {
    if (isNetworkView && networkId) {
      return `/v1/networks/${networkId}/recognitions`;
    }
    return `/v1/organisations/${organisation.id}/recognitions`;
  }, [location.pathname, networkId, organisation]);

  const {
    data: highFiveData,
    meta: metaData,
    loading,
    refetch,
    reset,
  } = useRequest(apiUrl, apiConfig);

  useEffect(() => {
    if (!didMount) return;
    reset();
  }, [payload, didMount, reset]);

  const meta: RecognitionMeta = metaData;
  const columns = useColumns(highFiveData, meta, loggedUser, isNetworkView);

  return (
    <Container name="high-fives">
      <TopNavigationBar title={t('organisation:content_high_fives')} />
      <Container.Content>
        <Bar>
          <FiltersToolbar
            {...(!isNetworkView && {
              predicates,
              setPredicates,
              organisation,
            })}
            onSearch={setSearch}
            searchPlaceholder={t('common:search_list_search_placeholder')}
          />
        </Bar>

        <Overview>
          <DataTable
            columns={columns}
            data={highFiveData}
            loading={loading}
            onShowMore={refetch}
            pagination={{
              ...omit(apiConfig, 'payload'),
              ...meta?.pagination,
            }}
            emptyView={
              <Placeholder
                title={t('organisation:no_high_fives_placeholder')}
              />
            }
          />
        </Overview>
      </Container.Content>
    </Container>
  );
};

export default HighFiveContainer;
